import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Image,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Divider
} from '@chakra-ui/react';
import { FiStar, FiTrendingUp, FiHeart, FiPlay, FiRefreshCw, FiThumbsUp } from 'react-icons/fi';
import { SearchDiscoveryService, type Recommendation } from '../services/searchDiscoveryService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';
import { useNavigate } from 'react-router-dom';

interface ContentRecommendationsProps {
  limit?: number;
  showHeader?: boolean;
}

const ContentRecommendations: React.FC<ContentRecommendationsProps> = ({
  limit = 12,
  showHeader = true
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (user) {
      loadRecommendations();
    }
  }, [user]);

  const loadRecommendations = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const recs = await SearchDiscoveryService.getRecommendations(user.uid, limit);
      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadRecommendations();
    setIsRefreshing(false);
  };

  const handleContentClick = (recommendation: Recommendation) => {
    if (recommendation.category === 'short') {
      navigate(`/short/${recommendation.id}`);
    } else {
      navigate(`/content/${recommendation.id}`);
    }
  };

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'similar':
        return FiThumbsUp;
      case 'trending':
        return FiTrendingUp;
      case 'popular':
        return FiHeart;
      case 'recent':
        return FiStar;
      default:
        return FiStar;
    }
  };

  const getRecommendationColor = (type: string) => {
    switch (type) {
      case 'similar':
        return 'blue';
      case 'trending':
        return 'red';
      case 'popular':
        return 'green';
      case 'recent':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'yellow';
    return 'gray';
  };

  const renderRecommendationCard = (recommendation: Recommendation) => (
    <Box
      key={recommendation.id}
      bg={cardBg}
      borderRadius="lg"
      overflow="hidden"
      border="1px"
      borderColor={borderColor}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      onClick={() => handleContentClick(recommendation)}
    >
      <Box position="relative">
        <Image
          src={recommendation.poster}
          alt={recommendation.title}
          w="100%"
          h="200px"
          objectFit="cover"
          fallbackSrc="/placeholder-poster.jpg"
        />
        <Badge
          position="absolute"
          top={2}
          left={2}
          colorScheme={getRecommendationColor(recommendation.type)}
          fontSize="xs"
          px={2}
          py={1}
        >
          <IconWrapper icon={getRecommendationIcon(recommendation.type)} size={10} style={{ marginRight: '4px' }} />
          {recommendation.type}
        </Badge>
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="purple"
          fontSize="xs"
        >
          {recommendation.category}
        </Badge>
      </Box>
      
      <VStack p={4} align="stretch" gap={2}>
        <Text fontWeight="bold" fontSize="sm" noOfLines={2}>
          {recommendation.title}
        </Text>
        
        <Text fontSize="xs" color="gray.500" noOfLines={2}>
          {recommendation.reason}
        </Text>
        
        <HStack justify="space-between">
          <Badge
            colorScheme={getScoreColor(recommendation.score)}
            fontSize="xs"
          >
            Match: {(recommendation.score * 100).toFixed(0)}%
          </Badge>
          <Button
            size="xs"
            colorScheme="purple"
            leftIcon={<IconWrapper icon={FiPlay} size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              handleContentClick(recommendation);
            }}
            whiteSpace="nowrap"
            minW="fit-content"
            px={3}
            flexShrink={0}
          >
            Watch
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  if (!user) {
    return (
      <Container maxW="container.xl">
        <Center py={8}>
          <VStack gap={2}>
            <Text color="gray.500">Please sign in to see personalized recommendations</Text>
            <Button size="sm" colorScheme="purple" onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </VStack>
        </Center>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      {showHeader && (
        <VStack gap={4} align="stretch" mb={6}>
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiStar} size={24} style={{ marginRight: '8px' }} />
              Recommended for You
            </Heading>
            <Tooltip label="Refresh recommendations">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={handleRefresh}
                isLoading={isRefreshing}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
          
          <Text color="gray.400" fontSize="sm">
            Based on your watch history and preferences
          </Text>
        </VStack>
      )}

      {recommendations.length === 0 ? (
        <Center py={8}>
          <VStack gap={2}>
            <Text color="gray.500">No recommendations available</Text>
            <Text fontSize="sm" color="gray.400">
              Start watching content to get personalized recommendations
            </Text>
            <Button size="sm" onClick={handleRefresh}>
              Try Again
            </Button>
          </VStack>
        </Center>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 6 }} gap={4}>
            {recommendations.map(renderRecommendationCard)}
          </SimpleGrid>
          
          <Divider my={6} />
          
          <VStack gap={2} align="center">
            <Text fontSize="sm" color="gray.500">
              Recommendations are updated based on your viewing activity
            </Text>
            <Button size="sm" variant="outline" onClick={handleRefresh}>
              Update Recommendations
            </Button>
          </VStack>
        </>
      )}
    </Container>
  );
};

export default ContentRecommendations;
